<template>
  <div class="pa-0 ma-0 fill-height admin-portal">
    <v-dialog
      v-model="overlay"
      hide-overlay
      persistent
      width="300"
      lazy
    >
      <v-card color="secondary" dark>
        <v-card-text>
          {{ overlayMessage }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <div v-if="!overlay" class="admin-portal-content">
      <v-row justify="center" class="ma-0">
        <v-col cols="12">
          <div class="admin-portal-shell mx-auto">
            <header class="admin-portal-header text-center">
              <h1 class="display-1 font-weight-medium mb-3">
                Admin Portal
              </h1>
              <p class="body-1 grey--text text--darken-1 mb-0">
                Admin-only tools for managing users, clusters, storyboards, saved searches, and platform monitoring.
              </p>
            </header>

            <v-row v-if="error" class="admin-portal-alerts ma-0">
              <v-col cols="12" class="pa-0">
                <v-alert type="error" dismissible @input="error = null">
                  <strong>Error:</strong> {{ error }}
                </v-alert>
              </v-col>
            </v-row>

            <v-row v-else-if="!isAdmin" class="admin-portal-alerts ma-0">
              <v-col cols="12" class="pa-0">
                <v-alert type="warning" outlined>
                  Admin access is required to use this portal.
                </v-alert>
              </v-col>
            </v-row>

            <v-row v-else class="admin-tool-grid ma-0">
              <v-col
                v-for="tool in adminTools"
                :key="tool.route"
                cols="12"
                sm="6"
                class="admin-tool-col"
              >
                <v-card
                  class="admin-tool-card d-flex flex-column"
                  elevation="1"
                  @click="goTo(tool.route)"
                >
                  <v-card-text class="flex-grow-1 pb-2">
                    <div class="admin-tool-icon mb-4">
                      <v-icon color="primary" size="28">
                        {{ tool.icon }}
                      </v-icon>
                    </div>
                    <div class="text-h6 font-weight-medium mb-2">
                      {{ tool.title }}
                    </div>
                    <div class="body-2 grey--text text--darken-1">
                      {{ tool.description }}
                    </div>
                  </v-card-text>
                  <v-card-actions class="pt-0 px-4 pb-4">
                    <v-btn
                      text
                      color="primary"
                      class="px-0 font-weight-medium"
                      @click.stop="goTo(tool.route)"
                    >
                      Open
                      <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { getCurrentUserProfile } from "@/components/APIManager.js";

export default {
  name: "AdminPortal",

  data() {
    return {
      overlay: true,
      overlayMessage: "Loading admin portal...",
      error: null,
      isAdmin: false,
      adminTools: [
        {
          title: "Cluster Administration",
          description: "Copy supplier and buyer clusters between users.",
          icon: "mdi-family-tree",
          route: "cluster-administration",
        },
        {
          title: "Storyboard Management",
          description: "Inspect user storyboards and copy them to another account.",
          icon: "mdi-view-dashboard-edit",
          route: "admin-storyboards",
        },
        {
          title: "Saved Search Management",
          description: "Review saved searches and copy them to another user.",
          icon: "mdi-content-save-search",
          route: "admin-saved-searches",
        },
        {
          title: "Monitoring",
          description: "Browse search activity, user history, and per-user search statistics.",
          icon: "mdi-monitor-dashboard",
          route: "admin-monitoring",
        },
      ],
    };
  },

  async mounted() {
    await this.loadCurrentUser();
    this.overlay = false;
  },

  methods: {
    async loadCurrentUser() {
      try {
        const response = await getCurrentUserProfile();
        if (response.status === "success") {
          this.isAdmin = response.data.tt_admin === true;
        } else {
          this.error = "Failed to load current user.";
        }
      } catch (error) {
        this.error = "Failed to load current user.";
        this.isAdmin = false;
      }
    },

    goTo(routeName) {
      this.$router.push({ name: routeName });
    },
  },
};
</script>

<style scoped>
.admin-portal {
  background-color: #f5f5f5;
}

.admin-portal-content {
  min-height: 100%;
}

.admin-portal-shell {
  max-width: 960px;
  padding: 32px 24px 40px;
}

.admin-portal-header {
  max-width: 640px;
  margin: 0 auto 32px;
}

.admin-portal-alerts {
  margin-bottom: 8px !important;
}

.admin-tool-grid {
  margin: -8px !important;
}

.admin-tool-col {
  padding: 8px !important;
}

.admin-tool-card {
  height: 100%;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.admin-tool-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.admin-tool-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: rgba(25, 118, 210, 0.1);
}
</style>
